import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiPlus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { PRIMARY } from '../../theme';
import api from '../../utils/api';
import { useReferenceData } from '../../contexts/ReferenceDataContext';

/** Matches Teams Events page primary actions (e.g. Add Team). */
const TEAMS_TOOLBAR_PRIMARY_BTN = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  flexShrink: 0,
  height: 34,
  padding: '0 14px',
  border: 'none',
  color: '#ffffff',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  boxShadow: '0 1px 2px rgba(78, 115, 223, 0.28)',
};

const normalizeJobTypes = (response) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
    ? response
    : [];

  return rows.map((item, index) => {
    if (typeof item === 'string') {
      return {
        id: index + 1,
        name: item,
      };
    }

    return {
      id: item._id || item.id || index + 1,
      name: item.name || item.jobType || item.title || '-',
    };
  });
};

const JobTypesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobTypesResponse, loading, refreshJobTypes } = useReferenceData();

  const handleBack = () => {
    const fromPath = String(location.state?.from || '').trim();
    navigate(fromPath || '/admin/settings');
  };
  const jobTypes = useMemo(
    () => normalizeJobTypes(jobTypesResponse || {}),
    [jobTypesResponse]
  );
  const [deletingId, setDeletingId] = useState('');
  const [newJobType, setNewJobType] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [editingValue, setEditingValue] = useState('');
  const [savingId, setSavingId] = useState('');

  const handleCreateJobType = async (event) => {
    event.preventDefault();
    const value = newJobType.trim();
    if (!value) {
      toast.error('Please enter job type');
      return;
    }

    try {
      setCreating(true);
      await api.createJobType(value);
      setNewJobType('');
      toast.success('Job type created successfully');
      await refreshJobTypes();
    } catch (error) {
      console.error('Error creating job type:', error);
      toast.error(error.message || 'Failed to create job type');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (jobType) => {
    setEditingId(jobType.id);
    setEditingValue(jobType.name || '');
  };

  const cancelEdit = () => {
    setEditingId('');
    setEditingValue('');
  };

  const handleSaveEdit = async (jobTypeId) => {
    const value = editingValue.trim();
    if (!value) {
      toast.error('Please enter job type');
      return;
    }

    try {
      setSavingId(jobTypeId);
      await api.updateJobType(jobTypeId, value);
      toast.success('Job type updated successfully');
      cancelEdit();
      await refreshJobTypes();
    } catch (error) {
      console.error('Error updating job type:', error);
      toast.error(error.message || 'Failed to update job type');
    } finally {
      setSavingId('');
    }
  };

  const handleDeleteJobType = async (jobType) => {
    const confirmed = window.confirm(`Delete job type "${jobType.name}"?`);
    if (!confirmed) return;

    try {
      setDeletingId(jobType.id);
      await api.deleteJobType(jobType.name);
      await refreshJobTypes();
      toast.success('Job type deleted successfully');
    } catch (error) {
      console.error('Error deleting job type:', error);
      toast.error(error.message || 'Failed to delete job type');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '14px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '0 1 auto', minWidth: 0, maxWidth: '100%' }}>
            <form
              onSubmit={handleCreateJobType}
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 8,
                maxWidth: '100%',
              }}
            >
              <input
                type="text"
                placeholder="Enter job type"
                value={newJobType}
                onChange={(e) => setNewJobType(e.target.value)}
                style={{
                  flex: '1 1 auto',
                  minWidth: 400,
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  height: 34,
                  padding: '0 10px',
                  border: '1px solid #dee2e6',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#344054',
                  background: '#fff',
                }}
              />
              <button
                type="submit"
                disabled={creating}
                style={{
                  ...TEAMS_TOOLBAR_PRIMARY_BTN,
                  flexShrink: 0,
                  background: PRIMARY,
                  cursor: creating ? 'not-allowed' : 'pointer',
                  opacity: creating ? 0.7 : 1,
                }}
              >
                <FiPlus size={15} strokeWidth={2.5} aria-hidden style={{ color: '#fff' }} />
                {creating ? 'Adding...' : 'Add'}
              </button>
            </form>
          </div>
          <button
            type="button"
            onClick={handleBack}
            style={{
              flex: '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              padding: '0 12px',
              border: '1px solid #e5e7eb',
              background: '#ffffff',
              color: '#1f2937',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 600,
              boxSizing: 'border-box',
            }}
          >
            <FiArrowLeft size={14} strokeWidth={2.2} aria-hidden />
            Back
          </button>
        </div>

        <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflowX: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#6b7280' }}>Job Type</th>
                <th style={{ textAlign: 'center', padding: '10px', fontSize: '12px', color: '#6b7280' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="2" style={{ padding: '14px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                    Loading job types...
                  </td>
                </tr>
              ) : jobTypes.length === 0 ? (
                <tr>
                  <td colSpan="2" style={{ padding: '14px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                    No job types found.
                  </td>
                </tr>
              ) : (
                jobTypes.map((jobType) => (
                  <tr key={jobType.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '10px', fontSize: '13px', color: '#111827', fontWeight: 600 }}>
                      {editingId === jobType.id ? (
                        <input
                          type="text"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          style={{
                            width: '100%',
                            maxWidth: '360px',
                            padding: '8px 10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 500,
                          }}
                        />
                      ) : (
                        jobType.name
                      )}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      {editingId === jobType.id ? (
                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => handleSaveEdit(jobType.id)}
                            disabled={savingId === jobType.id}
                            style={{
                              border: '1px solid #bfdbfe',
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              cursor: savingId === jobType.id ? 'not-allowed' : 'pointer',
                              opacity: savingId === jobType.id ? 0.7 : 1,
                              fontSize: '12px',
                            }}
                          >
                            {savingId === jobType.id ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            style={{
                              border: '1px solid #d1d5db',
                              background: 'white',
                              color: '#374151',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                          <span className="icon-tooltip-wrap">
                            <button
                              type="button"
                              aria-label="Edit"
                              onClick={() => startEdit(jobType)}
                              style={{
                                border: '1px solid #a7f3d0',
                                background: '#ecfeff',
                                color: '#0f766e',
                                borderRadius: '6px',
                                width: '28px',
                                height: '28px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                lineHeight: 1,
                              }}
                            >
                              ✏
                            </button>
                            <span className="icon-tooltip-bubble">Edit</span>
                          </span>
                          <span className="icon-tooltip-wrap">
                            <button
                              type="button"
                              aria-label="Delete"
                              onClick={() => handleDeleteJobType(jobType)}
                              disabled={deletingId === jobType.id}
                              style={{
                                border: '1px solid #fecdd3',
                                background: '#fff1f2',
                                color: '#e11d48',
                                borderRadius: '6px',
                                width: '28px',
                                height: '28px',
                                cursor: deletingId === jobType.id ? 'not-allowed' : 'pointer',
                                opacity: deletingId === jobType.id ? 0.6 : 1,
                                fontSize: '14px',
                                lineHeight: 1,
                              }}
                            >
                              🗑
                            </button>
                            <span className="icon-tooltip-bubble">Delete</span>
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default JobTypesPage;
